import { referralTableDataEnums } from "../../../domain/Referral/tableDataEnums"
import { RecordType } from "../components/AdvanceTable/advanceTable.types"
import { posthog } from "../../../posthogInstance"
import { FeatureFlag } from "../../../domain/enums/featureFlags"
export interface ReferralAndSessionStatusCount {
  referralStatusCounts: {
    new: number
    active: number
    closed: number
  }
  sessionStatusCounts: {
    scheduled: number
    completed: number
    cancelled: number
    "no-show": number
  }
  hasFeeReduction: boolean
}

export interface ReferralSummaryData {
  id: string
  referralStatus: string
  referralDate: string
  caseNumber: number
  referredBy: string
  riskCode: string
  memberName: string
  providerId: string
  providerName: string
  sessions: string
  feeReductionStatus: string
  canShowDetails: boolean
}

export interface ReferralSessionCSVItem {
  id: string
  caseNumber: number
  adpSessionId: number
  sessionDateTime: string
  createdDateTime: string
  createdBy: string
  updatedDateTime: string
  updatedBy?: string
  location?: string
  locationId: string
  sessionStatusDisplayId: string
  sessionTypeDisplayId: string
  feeReduction: string
  referralExternalId: string
  practiceId: string
  practiceName: string
  referralCode: string
  providerName: string
  sessionArchivedBy?: string
  sessionArchivedDateTime?: string
  referralArchivedBy?: string
  referralArchivedDatetime?: string
}

export interface ReferralSessionItem {
  id: string
  adpSessionId: number
  sessionDateTime: string
  location?: string
  locationId: string
  sessionStatusDisplayId: string
  sessionTypeDisplayId: string
  feeReduction: string
  referralExternalId: string
  practiceId: string
  practiceName: string
  referralCode: string
  providerName: string
  referralArchivedDatetime?: string
}

export interface PaginatedReferralSessions {
  totalCount: number
  items: ReferralSessionItem[]
}

export interface ProviderCount {
  providerName: string
  count: number
}

export type ReferralStatusCount = {
  statusCounts: {
    [K in keyof typeof referralTableDataEnums.referralStatuses]: number
  }
  providerCounts: Record<string, ProviderCount>
}

export interface ReferralTableData extends RecordType {
  id: string
  referralStatus: keyof typeof referralTableDataEnums.referralStatuses
  referralDate: string
  caseNumber: number
  referredBy: string
  memberName: string
  providerName: string
  sessions: string
  feeReduction: string
  canShowDetails: boolean
  referralCode: string
  closeReason: string
  sourceReferralId: string | null
  sessionLimitLastModifiedBy: string | null
  sessionLimitLastModifiedTime: string | null
}

export interface TransferInfoDto {
  sourceProviderId: string
  targetProviderId: string
  transferReasonDisplayId: string
  transferDate: string
}
import {
  ReferralLogEntry,
  ReferralQueryParams,
  Referrals,
  ReferralStats,
  ReferralSummary,
  ReferralSummaryList,
  ReferralWrite,
} from "../../../domain/Referral/schema"
import { getRouteWithQueryParams } from "../../../routes"
import {
  getData,
  buildURLWithQueryParams,
  buildUrl,
  putData,
  deleteData,
} from "../apiService"

const apiRoutes = {
  get: {
    REFERRALS: "/referral/referrals",
    REFERRAL_SUMMARY: "/referral/referralSummary",
    REFERRAL_SUMMARY_BY_ID: "/referral/referralSummary/:referralId",
    REFERRAL_SUMMARY_BY_CASE_NUMBER:
      "/referral/referralSummary/case/:caseNumber",
    REFERRAL_SUMMARY_LOG_BY_CASE_NUMBER:
      "/referral/referralLog/case/:caseNumber/export",
    REFERRAL_PACKET_BY_CASEID: "/referral/referralPacket/:caseId",
    REFERRAL_SUMMARY_BY_PRACTICE_ID:
      "/referral/referralSummary/practice/:practiceId",
    REFERRAL_SUMMARY_STATUS_COUNTS_BY_PRACTICE_ID:
      "/referral/referralSummary/practice/:practiceId/statuscounts",
    REFERRAL_SECURE_CASE_COUNT_BY_PRACTICE_ID:
      "/referral/referralSummary/practice/:practiceId/referralSecureCaseCount",
    REFERRAL_AND_SESSION_STATUS_COUNTS_BY_PRACTICE_ID:
      "/referral/referralSummary/practice/:practiceId/referralAndSessionStatusCounts",
    COMPLETED_SESSION_COUNT_BY_CASEID:
      "/referral/completedSessionCount/:caseId",
    REFERRAL_STATS: "/referral/referralStats/:caseId",
    TRANSFER_INFO_BY_REFERRAL_ID:
      "/referral/referrals/:referralId/transferInfo",
    REFERRAL_SESSIONS_BY_CASE_NUMBER_EXPORT:
      "/referral/referralSessions/case/:caseNumber/export",
  },
  post: {
    REFERRALS: "/referral/referrals",
    REFERRALS_SESSIONS: "/referral/referrals/:referralId/sessions",
    TRANSFER_REFERRAL: "/referral/referrals/:referralId/transfer",
    REFERRAL_SUMMARY_LIST_BY_CASE_NUMBER:
      "/referral/referralSummary/case/:caseNumber",
    REFERRAL_SESSIONS_BY_CASE_NUMBER:
      "/referral/referralSessions/case/:caseNumber",
  },
  put: {
    REFERRALS: "/referral/referrals/:referralId",
    REFERRALS_SESSIONS: "/referral/referrals/:referralId/sessions/:sessionId",
  },
  delete: {
    REFERRALS: "/referral/referrals/:referralId",
    REFERRALS_SESSIONS: "/referral/referrals/:referralId/sessions/:sessionId",
  },
}

const deleteReferral = async ({ referralId }: { referralId: string }) => {
  return deleteData(
    buildURLWithQueryParams(apiRoutes.delete.REFERRALS, {
      referralId,
    }),
  )
}

const deleteReferralSession = async (
  referralId: string,
  sessionId: string,
  apiVersion: string = "1.0",
) => {
  return deleteData(
    buildURLWithQueryParams(
      apiRoutes.delete.REFERRALS_SESSIONS,
      {
        referralId,
        sessionId,
      },
      { "api-version": apiVersion },
    ),
  )
}

export interface PaginatedReferralSummary {
  totalCount: number
  items: ReferralSummary[]
}

const getPaginatedReferralSummaryByPracticeId = async ({
  practiceId,
  body,
}: {
  practiceId: string
  body: {
    filter: string[]
    providerFilter: string[]
    sortBy: {
      columnName: string
      isDescending: boolean
    }
    count: number
    offset: number
  }
}) => {
  return getData<PaginatedReferralSummary>(
    buildUrl(apiRoutes.get.REFERRAL_SUMMARY_BY_PRACTICE_ID, {
      practiceId,
    }),
    true,
    body,
  )
}

const getReferralAndSessionStatusCountByPracticeId = async ({
  practiceId,
  providerId,
}: {
  practiceId: string
  providerId?: string
}) => {
  const urlParamRecord: Record<string, string> = { practiceId }
  const queryParamRecord: Record<string, string> = {}
  if (providerId) {
    queryParamRecord["providerId"] = providerId
  }
  return getData<ReferralAndSessionStatusCount>(
    buildURLWithQueryParams(
      apiRoutes.get.REFERRAL_AND_SESSION_STATUS_COUNTS_BY_PRACTICE_ID,
      urlParamRecord,
      queryParamRecord,
    ),
  )
}

const getReferralSummary = async (practiceId: string) => {
  return getData<ReferralSummaryList>(
    getRouteWithQueryParams(apiRoutes.get.REFERRAL_SUMMARY, {
      practiceId,
    }),
  )
}

const getReferralSummaryById = async (referralId: string) => {
  return getData<ReferralSummary>(
    buildUrl(apiRoutes.get.REFERRAL_SUMMARY_BY_ID, { referralId }),
  )
}

const getReferralSummaryByCaseNumber = async (caseNumber: string) => {
  return getData<ReferralSummary>(
    buildUrl(apiRoutes.get.REFERRAL_SUMMARY_BY_CASE_NUMBER, { caseNumber }),
  )
}

const getReferralSummaryLogByCaseNumber = async (caseNumber: string) => {
  const version = posthog.isFeatureEnabled(
    FeatureFlag.IsCaseViewExportUserName19866Enabled,
  )
    ? "2.0"
    : "1.0"
  return getData<ReferralLogEntry[]>(
    buildURLWithQueryParams(
      apiRoutes.get.REFERRAL_SUMMARY_LOG_BY_CASE_NUMBER,
      { caseNumber },
      { "api-version": version },
    ),
  )
}

const getPaginatedReferralSummaryByCaseNumber = async ({
  caseNumber,
  body,
}: {
  caseNumber: string
  body: {
    sortBy: { columnName: string; isDescending: boolean }
    count: number
    offset: number
  }
}) => {
  return getData<PaginatedReferralSummary>(
    buildUrl(apiRoutes.post.REFERRAL_SUMMARY_LIST_BY_CASE_NUMBER, {
      caseNumber,
    }),
    true,
    body,
  )
}

const getPaginatedReferralSessionsByCaseNumber = async ({
  caseNumber,
  body,
}: {
  caseNumber: string
  body: { count: number; offset: number }
}) => {
  return getData<PaginatedReferralSessions>(
    buildUrl(apiRoutes.post.REFERRAL_SESSIONS_BY_CASE_NUMBER, {
      caseNumber,
    }),
    true,
    body,
  )
}

const getReferralSessionsByCaseNumberExport = async ({
  caseNumber,
}: {
  caseNumber: string
}) => {
  const version = posthog.isFeatureEnabled(
    FeatureFlag.IsCaseViewExportUserName19866Enabled,
  )
    ? "2.0"
    : "1.0"
  return getData<ReferralSessionCSVItem[]>(
    buildURLWithQueryParams(
      apiRoutes.get.REFERRAL_SESSIONS_BY_CASE_NUMBER_EXPORT,
      { caseNumber },
      { "api-version": version },
    ),
  )
}

const getReferralSummaryStatusCountByPracticeId = async ({
  practiceId,
}: {
  practiceId: string
}) => {
  const urlParamRecord: Record<string, string> = { practiceId }
  const queryParamRecord: Record<string, string> = {}
  return getData<ReferralStatusCount>(
    buildURLWithQueryParams(
      apiRoutes.get.REFERRAL_SUMMARY_STATUS_COUNTS_BY_PRACTICE_ID,
      urlParamRecord,
      queryParamRecord,
    ),
  )
}

const getReferralSecureCaseCountByPracticeId = async ({
  practiceId,
}: {
  practiceId: string
}) => {
  const urlParamRecord: Record<string, string> = { practiceId }
  const queryParamRecord: Record<string, string> = {}
  return getData<number>(
    buildURLWithQueryParams(
      apiRoutes.get.REFERRAL_SECURE_CASE_COUNT_BY_PRACTICE_ID,
      urlParamRecord,
      queryParamRecord,
    ),
  )
}

const getCompletedSessionCountByCaseId = async (caseId: string) => {
  return getData<number>(
    buildUrl(apiRoutes.get.COMPLETED_SESSION_COUNT_BY_CASEID, { caseId }),
  )
}

const referral_stats = async (caseId: string, includeArchived = false) => {
  return getData<ReferralStats[]>(
    buildURLWithQueryParams(
      apiRoutes.get.REFERRAL_STATS,
      { caseId },
      includeArchived ? { includeArchived: "true" } : undefined,
    ),
  )
}

const postReferral = async (data: ReferralWrite) => {
  return getData(buildURLWithQueryParams(apiRoutes.post.REFERRALS), true, data)
}

const postTransferReferral = async ({
  referralId,
  targetProviderId,
  locationId,
  reason,
  counselingRate,
  expirationDays,
  isUnderSupervision,
}: {
  referralId: string
  targetProviderId: string
  locationId?: string | null
  reason?: string
  counselingRate: number
  expirationDays: number
  isUnderSupervision: boolean
}) => {
  const body = {
    targetProviderId,
    locationId: locationId || null,
    transferReasonDisplayId: reason || null,
    counselingRate: counselingRate,
    expirationDays: expirationDays,
    isUnderSupervision: isUnderSupervision,
  }
  return getData(
    buildURLWithQueryParams(apiRoutes.post.TRANSFER_REFERRAL, { referralId }),
    true,
    body,
  )
}

const postReferralSession = async (
  referralId: string,
  data: unknown,
  apiVersion: string = "1.0",
) => {
  return getData(
    buildURLWithQueryParams(
      apiRoutes.post.REFERRALS_SESSIONS,
      {
        referralId,
      },
      { "api-version": apiVersion },
    ),
    true,
    data,
  )
}

const putReferral = async ({
  referralId,
  data,
}: {
  referralId: string
  data: unknown
}) => {
  return putData(
    buildURLWithQueryParams(apiRoutes.put.REFERRALS, { referralId }),
    data,
  )
}

const putReferralSession = async (
  referralId: string,
  sessionId: string,
  data: unknown,
  apiVersion: string = "1.0",
) => {
  return putData(
    buildURLWithQueryParams(
      apiRoutes.put.REFERRALS_SESSIONS,
      {
        referralId,
        sessionId,
      },
      { "api-version": apiVersion },
    ),
    data,
  )
}

const referralPacket = async (caseId: string, includeMemberIntake = false) => {
  return getData(
    buildURLWithQueryParams(
      apiRoutes.get.REFERRAL_PACKET_BY_CASEID,
      {
        caseId,
      },
      includeMemberIntake ? { includeMemberIntake: "true" } : undefined,
    ),
  )
}

const referrals = async (
  queryParams: ReferralQueryParams,
): Promise<Referrals> => {
  return getData(
    buildURLWithQueryParams(apiRoutes.get.REFERRALS, undefined, queryParams),
  )
}

const getTransferInfo = async (referralId: string) => {
  return getData<TransferInfoDto>(
    buildUrl(apiRoutes.get.TRANSFER_INFO_BY_REFERRAL_ID, { referralId }),
  )
}

export const referralApi = {
  get: {
    REFERRAL_AND_SESSION_STATUS_COUNTS_BY_PRACTICE_ID:
      getReferralAndSessionStatusCountByPracticeId,
    REFERRAL_PACKET_BY_CASEID: referralPacket,
    REFERRAL_SUMMARY: getReferralSummary,
    REFERRAL_SUMMARY_BY_ID: getReferralSummaryById,
    REFERRAL_SUMMARY_BY_CASE_NUMBER: getReferralSummaryByCaseNumber,
    REFERRAL_SUMMARY_LOG_BY_CASE_NUMBER: getReferralSummaryLogByCaseNumber,
    REFERRAL_SUMMARY_STATUS_COUNTS_BY_PRACTICE_ID:
      getReferralSummaryStatusCountByPracticeId,
    REFERRAL_SECURE_CASE_COUNT_BY_PRACTICE_ID:
      getReferralSecureCaseCountByPracticeId,
    REFERRALS: referrals,
    COMPLETED_SESSION_COUNT_BY_CASEID: getCompletedSessionCountByCaseId,
    REFERRAL_STATS: referral_stats,
    TRANSFER_INFO_BY_REFERRAL_ID: getTransferInfo,
    REFERRAL_SESSIONS_BY_CASE_NUMBER_EXPORT:
      getReferralSessionsByCaseNumberExport,
  },
  post: {
    PAGINATED_REFERRAL_SUMMARY_BY_PRACTICE_ID:
      getPaginatedReferralSummaryByPracticeId,
    PAGINATED_REFERRAL_SUMMARY_BY_CASE_NUMBER:
      getPaginatedReferralSummaryByCaseNumber,
    PAGINATED_REFERRAL_SESSIONS_BY_CASE_NUMBER:
      getPaginatedReferralSessionsByCaseNumber,
    REFERRALS: postReferral,
    REFERRALS_SESSIONS: postReferralSession,
    TRANSFER_REFERRAL: postTransferReferral,
  },
  put: {
    REFERRALS: putReferral,
    REFERRALS_SESSIONS: putReferralSession,
  },
  delete: {
    REFERRALS: deleteReferral,
    REFERRALS_SESSIONS: deleteReferralSession,
  },
}
